import { getApsAuthBase } from "../../auth/apsConfig";
import { dmFetch } from "./client";
import type { DmAdsRegion } from "./region";

/** Split `urn:adsk.objects:os.object:{bucketKey}/{objectKey}` from storage create. */
export function parseObjectStorageUrn(objectId: string): {
  bucketKey: string;
  objectKey: string;
} {
  const prefix = "urn:adsk.objects:os.object:";
  if (!objectId.startsWith(prefix)) {
    throw new Error("Unexpected APS storage URN shape.");
  }
  const rest = objectId.slice(prefix.length);
  const slash = rest.indexOf("/");
  if (slash <= 0 || slash === rest.length - 1) {
    throw new Error("Unexpected APS storage URN shape.");
  }
  return {
    bucketKey: rest.slice(0, slash),
    objectKey: rest.slice(slash + 1),
  };
}

interface SignedS3UploadResponse {
  uploadKey?: string;
  urls?: string[];
  url?: string;
}

export async function uploadBytesToSignedOss(
  accessToken: string,
  bucketKey: string,
  objectKey: string,
  bytes: ArrayBuffer,
  contentType: string,
  region?: DmAdsRegion
): Promise<void> {
  const base = getApsAuthBase();
  const encBucket = encodeURIComponent(bucketKey);
  const encObject = encodeURIComponent(objectKey);
  const signedPath = `/oss/v2/buckets/${encBucket}/objects/${encObject}/signeds3upload`;

  const signed = await dmFetch<SignedS3UploadResponse>(
    accessToken,
    signedPath,
    {
      region,
      init: { method: "GET" },
    }
  );

  const uploadUrl = signed.urls?.[0] ?? signed.url;
  const uploadKey = signed.uploadKey;
  if (!uploadUrl || !uploadKey) {
    throw new Error("APS did not return a signed upload URL.");
  }

  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: bytes,
  });
  if (!putRes.ok) {
    throw new Error(`OSS upload failed (${putRes.status}).`);
  }

  const completeUrl = `${base}${signedPath}`;
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };
  if (region) headers["x-ads-region"] = region;

  const completeRes = await fetch(completeUrl, {
    method: "POST",
    headers,
    body: JSON.stringify({ uploadKey }),
  });
  if (!completeRes.ok) {
    const body = await completeRes.text().catch(() => "");
    throw new Error(
      `OSS upload completion failed (${completeRes.status}): ${body || completeRes.statusText}`
    );
  }
}

interface SignedS3DownloadResponse {
  status?: string;
  url?: string;
}

/** Download ACC file bytes via OSS (works when DM downloadFormats 404s for PDFs). */
export async function downloadObjectViaSignedS3(
  accessToken: string,
  storageObjectUrn: string,
  region?: DmAdsRegion
): Promise<Blob> {
  const { bucketKey, objectKey } = parseObjectStorageUrn(storageObjectUrn);
  const encBucket = encodeURIComponent(bucketKey);
  const encObject = encodeURIComponent(objectKey);
  const path = `/oss/v2/buckets/${encBucket}/objects/${encObject}/signeds3download`;

  const signed = await dmFetch<SignedS3DownloadResponse>(accessToken, path, {
    region,
    init: { method: "GET" },
  });

  const url = signed.url;
  if (!url) {
    throw new Error("APS did not return a signed download URL.");
  }

  const dl = await fetch(url);
  if (!dl.ok) {
    throw new Error(`File download failed (${dl.status}).`);
  }
  return dl.blob();
}
